import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Pagination from "../../components/Pagination/Pagination";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { joinName, hasSecondaryCarer, isActivationPending, getApprovalStatus, getAccountActivation } from "../../components/family/familyDisplay";
import { API_CONFIG } from "../../config/api";
import {
  familiesService,
  registrationsService,
} from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { confirmAction, promptAction } from "../../utils/confirm";
import { showToast } from "../../utils/toast";
import { formatDate } from "../../utils/date";
import { takePageFilter, peekPageFilter } from "../../utils/listNavigation";

const VIEW_FILTERS = [
  { id: "pending", label: "Pending registrations", source: "registration", perm: "registrations" },
  { id: "rejected", label: "Rejected registrations", source: "registration", perm: "registrations" },
  { id: "active", label: "Active families", source: "family", status: 1, perm: "families" },
  { id: "inactive", label: "Inactive families", source: "family", status: 0, perm: "families" },
  { id: "all", label: "All records", source: "family", perm: "families" },
];

const regStatus = (row) => row?.registration_status || row?.status;
const rowName = (row) =>
  joinName(row?.primary_first_name, row?.primary_last_name, row?.full_name || row?.parent_name);

const getDefaultFilter = (filters) => {
  if (filters.some((f) => f.id === "all")) return "all";
  if (filters.some((f) => f.id === "pending")) return "pending";
  return filters[0]?.id || "all";
};

const STATUS_BADGE = {
  pending: "bg-warning text-dark",
  approved: "bg-success",
  rejected: "bg-secondary",
};

const ACTIVATION_BADGE = {
  pending: "bg-warning text-dark",
  password_set: "bg-info text-dark",
  active: "bg-success",
};

export default function Families() {
  const navigate = useNavigate();
  const location = useLocation();
  const { can, isGroupAdmin, permissionsByCode } = usePermission();
  const canRegUpdate = can("registrations", "is_update");
  const canFamUpdate = can("families", "is_update");
  const canFamDelete = can("families", "is_delete");

  const visibleFilters = useMemo(
    () =>
      VIEW_FILTERS.filter((f) => {
        const row = permissionsByCode[f.perm];
        return row && Number(row.is_view) === 1;
      }),
    [permissionsByCode]
  );

  const [viewFilter, setViewFilter] = useState("all");
  const fetchGenRef = useRef(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: API_CONFIG.PAGINATION.DEFAULT_LIMIT,
    total: 0,
  });
  const [busyId, setBusyId] = useState(null);

  const currentFilter = visibleFilters.find((f) => f.id === viewFilter) || visibleFilters[0];
  const isRegistrationView = currentFilter?.source === "registration";

  useEffect(() => {
    if (visibleFilters.length === 0) return;

    const incoming =
      peekPageFilter("/families") ?? location.state?.filter ?? null;

    if (incoming && visibleFilters.some((f) => f.id === incoming)) {
      takePageFilter("/families");
      setViewFilter(incoming);
      return;
    }

    setViewFilter((current) =>
      visibleFilters.some((f) => f.id === current)
        ? current
        : getDefaultFilter(visibleFilters)
    );
  }, [location.key, location.state, visibleFilters]);

  const fetchRows = useCallback(
    async (params = {}) => {
      const filterId = params.filter ?? viewFilter;
      const filter = VIEW_FILTERS.find((f) => f.id === filterId);
      if (!filter || !visibleFilters.some((f) => f.id === filterId)) return;

      const gen = ++fetchGenRef.current;
      setLoading(true);
      setError("");
      const page = params.page ?? 1;
      const limit = params.limit ?? pagination.limit;
      const searchVal = params.search ?? search;

      try {
        let res;
        if (filter.source === "registration") {
          res = await registrationsService.getAll({
            page,
            limit,
            search: searchVal,
            status: filter.id,
          });
        } else if (filter.id === "all") {
          res = await familiesService.getAll({
            page,
            limit,
            search: searchVal,
            mode: "all",
          });
        } else {
          const body = { page, limit, search: searchVal };
          if (filter.id !== "all") {
            body.status = filter.status;
          }
          res = await familiesService.getAll(body);
        }

        if (gen !== fetchGenRef.current) return;

        const list = res?.data?.list || (Array.isArray(res?.data) ? res.data : []);
        const total = res?.data?.total ?? list.length;
        setRows(list);
        setPagination({ page, limit, total });
      } catch (err) {
        if (gen !== fetchGenRef.current) return;
        setError(err.response?.data?.msg || err.message || "Failed to load");
        setRows([]);
      } finally {
        if (gen === fetchGenRef.current) setLoading(false);
      }
    },
    [viewFilter, search, pagination.limit, visibleFilters]
  );

  useEffect(() => {
    if (!visibleFilters.some((f) => f.id === viewFilter)) return;
    fetchRows({ page: 1, filter: viewFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter, visibleFilters]);

  const openEdit = (row) => {
    navigate(`/families/${row.id}/edit`);
  };

  const openChildren = (row) => {
    navigate(`/families/${row.id}/edit?tab=children`);
  };

  const toggleStatus = async (row) => {
    const res = await familiesService.updateStatus(row.id, row.is_active ? 0 : 1);
    if (res?.success) {
      showToast("success", "Status updated");
      fetchRows({ page: pagination.page, filter: viewFilter, search });
    }
  };

  const sendReset = async (row) => {
    const res = await familiesService.sendResetLink(row.id);
    if (res?.success) showToast("success", "Activation link sent to " + row.email);
    else showToast("error", res?.msg || "Failed");
  };

  const handleDelete = async (row) => {
    const r = await confirmAction({
      title: `Delete ${row.full_name}?`,
      text: "This removes the family and their children.",
      icon: "warning",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!r.isConfirmed) return;
    const res = await familiesService.delete(row.id);
    if (res?.success) {
      showToast("success", "Family deleted");
      fetchRows({ page: pagination.page, filter: viewFilter, search });
    } else showToast("error", res?.msg || "Failed");
  };

  const approve = async (row) => {
    const result = await confirmAction({
      title: `Approve ${rowName(row)}?`,
      text: "This creates a family account and emails a set-password link.",
      icon: "question",
      confirmText: "Approve",
      variant: "success",
    });
    if (!result.isConfirmed) return;
    setBusyId(row.id);
    try {
      const res = await registrationsService.approve(row.id);
      if (res?.success) {
        showToast("success", "Approved — approval email sent");
        fetchRows({ page: pagination.page, filter: viewFilter, search });
      } else {
        showToast("error", res?.msg || "Failed to approve");
      }
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (row) => {
    const { value: reason, isConfirmed } = await promptAction({
      title: `Reject ${rowName(row)}?`,
      text: "The registration will be marked rejected and the parent will receive an email notification.",
      inputLabel: "Reason (optional, included in the email)",
      inputPlaceholder: "e.g. We couldn't match this child to an enrolment",
      confirmText: "Reject",
      variant: "danger",
    });
    if (!isConfirmed) return;
    setBusyId(row.id);
    try {
      const res = await registrationsService.reject(row.id, reason || "");
      if (res?.success) {
        showToast("success", "Rejected — notification email sent");
        fetchRows({ page: pagination.page, filter: viewFilter, search });
      } else {
        showToast("error", res?.msg || "Failed to reject");
      }
    } finally {
      setBusyId(null);
    }
  };

  const resendApproval = async (row) => {
    const res = await registrationsService.resend(row.id);
    if (res?.success) showToast("success", "Approval email re-sent");
    else showToast("error", res?.msg || "Failed to resend");
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchRows({ page: 1, filter: viewFilter, search });
  };

  const handleFilterChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const onReset = (e) => {
    e.preventDefault();
    const defaultFilter = getDefaultFilter(visibleFilters);
    setViewFilter(defaultFilter);
    setSearch("");
    fetchRows({ page: 1, filter: defaultFilter, search: "" });
  };

  const emptyMessage = isRegistrationView
    ? viewFilter === "pending"
      ? "No pending registrations"
      : "No rejected registrations"
    : viewFilter === "inactive"
      ? "No inactive families"
      : viewFilter === "all"
        ? "No records yet"
        : "No active families";

  const searchPlaceholder = isRegistrationView
    ? "Parent, email or child name…"
    : "Name, email or phone…";

  const colSpan = 8;

  const renderRegistrationActions = (r) => {
    const status = regStatus(r);
    return (
      <>
        {status === "pending" && canRegUpdate && (
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-primary"
              disabled={busyId === r.id}
              onClick={() => approve(r)}
            >
              Approve
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              disabled={busyId === r.id}
              onClick={() => reject(r)}
            >
              Reject
            </button>
          </div>
        )}
        {status === "approved" && canRegUpdate && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => resendApproval(r)}
          >
            Resend link
          </button>
        )}
        {status === "rejected" && (
          <span className="text-muted small">{r.reject_reason || "—"}</span>
        )}
      </>
    );
  };

  const renderApprovalStatus = (r) => {
    const { key, label } = getApprovalStatus(r);
    if (key === "unknown") return <span className="text-muted">—</span>;
    return <span className={`badge ${STATUS_BADGE[key] || "bg-secondary"}`}>{label}</span>;
  };

  const renderAccountActivation = (r) => {
    const activation = getAccountActivation(r);
    if (!activation) return <span className="text-muted">—</span>;
    return (
      <span className={`badge ${ACTIVATION_BADGE[activation.key] || "bg-secondary"}`}>
        {activation.label}
      </span>
    );
  };

  const renderRowActions = (r) => {
    const status = regStatus(r);
    if (status === "pending" || status === "rejected") return renderRegistrationActions(r);
    return renderFamilyActions(r);
  };

  const renderFamilyActions = (r) => (
    <div className="d-flex flex-wrap gap-1">
      {canFamUpdate && (
        <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>
          Edit
        </button>
      )}
      {canFamUpdate && isActivationPending(r) && (
        <button className="btn btn-sm btn-outline-secondary" onClick={() => sendReset(r)}>
          Reset link
        </button>
      )}
      {canFamUpdate && (
        <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleStatus(r)}>
          {r.is_active ? "Disable" : "Enable"}
        </button>
      )}
      {canFamDelete && isGroupAdmin && (
        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>
          Delete
        </button>
      )}
    </div>
  );

  return (
    <div className="card mb-4">
      <div className="card-header">
        <strong>Families</strong>
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          {visibleFilters.length > 0 && (
            <FilterField id="fam-filter" label="Show">
              <select
                id="fam-filter"
                className="form-select"
                value={viewFilter}
                onChange={handleFilterChange}
              >
                {visibleFilters.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </FilterField>
          )}
          <FilterField id="fam-search" label="Search">
            <input
              id="fam-search"
              className="form-control"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              {isRegistrationView ? (
                <tr>
                  <th>Parent</th>
                  <th>Email / Phone</th>
                  <th>Child</th>
                  <th>Centre</th>
                  <th>Submitted</th>
                  <th>Approval Status</th>
                  <th>Account Activation</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              ) : (
                <tr>
                  <th>Family</th>
                  <th>Primary contact</th>
                  <th>Secondary carer</th>
                  <th>Centre</th>
                  <th>Children</th>
                  <th>Approval Status</th>
                  <th>Account Activation</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={colSpan} className="text-center py-4">Loading…</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className="text-center py-4 text-muted">
                    {emptyMessage}
                  </td>
                </tr>
              )}
              {!loading && isRegistrationView &&
                rows.map((r) => (
                  <tr key={`reg-${r.id}`}>
                    <td className="fw-semibold">{rowName(r)}</td>
                    <td>
                      <div>{r.email}</div>
                      <div className="small text-muted">{r.phone || "—"}</div>
                    </td>
                    <td>{r.child_name || "—"}</td>
                    <td>{r.centre || r.centre_name || "—"}</td>
                    <td className="small text-muted">{formatDate(r.created_at)}</td>
                    <td>{renderApprovalStatus(r)}</td>
                    <td>{renderAccountActivation(r)}</td>
                    <td>{renderRegistrationActions(r)}</td>
                  </tr>
                ))}
              {!loading && !isRegistrationView &&
                rows.map((r) => (
                  <tr key={`fam-${r.id}`}>
                    <td className="fw-semibold">
                      <div>{rowName(r)}</div>
                      {r.primary_relationship && (
                        <div className="small text-muted">{r.primary_relationship}</div>
                      )}
                    </td>
                    <td>
                      <div>{r.email}</div>
                      <div className="small text-muted">{r.phone || "—"}</div>
                    </td>
                    <td>
                      {hasSecondaryCarer(r) ? (
                        <>
                          <div>{joinName(r.secondary_first_name, r.secondary_last_name)}</div>
                          {r.secondary_relationship && (
                            <div className="small text-muted">{r.secondary_relationship}</div>
                          )}
                          <div className="small text-muted">
                            {[r.secondary_phone, r.secondary_email].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{r.centre || "—"}</td>
                    <td>
                      {regStatus(r) === "approved" ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openChildren(r)}
                        >
                          {r.children_count || 0}{" "}
                          {(r.children_count || 0) === 1 ? "child" : "children"}
                        </button>
                      ) : (
                        r.child_name || "—"
                      )}
                    </td>
                    <td>{renderApprovalStatus(r)}</td>
                    <td>{renderAccountActivation(r)}</td>
                    <td>{renderRowActions(r)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          itemsCount={rows.length}
          loading={loading}
          onPageChange={(page) => fetchRows({ page, filter: viewFilter, search })}
          onLimitChange={(limit) => fetchRows({ page: 1, limit, filter: viewFilter, search })}
        />
      </div>
    </div>
  );
}
